import React, {useEffect, useState} from "react";
import {Button, message, Modal, Steps, Table, Tooltip} from "antd";
import {ExclamationCircleOutlined, LeftOutlined, RightOutlined} from "@ant-design/icons";
import ProjectConfiguration from "../../../projectConfiguration/ProjectConfiguration";
import ProductApproval from "./ProductApproval";
import {JobCode} from "../../../../../types/JobCodes";
import {DeviData} from "../../../../../types/DeviData";
import OrderCardConfiguration from "../../../orderCardConfiguration/OrderCardConfiguration";
import OGLConfiguration from "../../../oglConfiguration/OGLConfiguration";
import axios from "axios";
import {getConfig} from "../../../../../config";
import ConstructionSectionParser from "../../../constructionSectionParser /jobCodeAssignment/ConstructionSectionParser";
import ProductAssignment from "../../../constructionSectionParser /productAssignment/ProductAssignment";
import OGLApproval from "./OGLApproval";
import {ViewType} from "../../../../../types/ViewType";
import {showUnsavedChangesConfirm} from "../../../../../utils/UI";

const { Step } = Steps;
const { API_BASE_URL } = getConfig();

interface ProjectStatusWizardProps {
    visible: boolean;
    onClose: () => void;
    onCompletion: () => void;
    selectedProject: string;
}

const TransferProjectWizard: React.FC<ProjectStatusWizardProps> = ({ visible, onClose, onCompletion, selectedProject }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [deviData, setDeviData] = useState<DeviData[]>([]);
    const [filteredData, setFilteredData] = useState<DeviData[]>([]);
    const [jobCodes, setJobCodes] = useState<JobCode[]>([]);
    const [oglCodes, setOglCodes] = useState<OGLCode[]>([]);
    const [orderCardData, setOrderCardData] = useState<OrderCardData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [projectConfigData, setProjectConfigData] = useState<ProjectData | null>(null);

    useEffect(() => {
        if (visible) {
            setCurrentStep(0);
            if (selectedProject) {
                fetchData(selectedProject);
            }
        }
    }, [visible, selectedProject]);

    const fetchData = async (projectNo: string) => {
        setIsLoading(true);
        try {
            const [deviResponse, jobCodeResponse, oglResponse, orderCardResponse] = await Promise.all([
                axios.get<DeviData[]>(`${API_BASE_URL}/getNetworkPlanDeviPositions?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectNo}&supplementNo=0`),
                axios.get<JobCode[]>(`${API_BASE_URL}/getNetworkPlanJobCodes?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectNo}`),
                axios.get<OGLCode[]>(`${API_BASE_URL}/getNetworkPlanOGLCodes?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectNo}`),
                axios.get<OrderCardData[]>(`${API_BASE_URL}/getNetworkPlanOrderCardData?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectNo}`)
            ]);
            setDeviData(deviResponse.data);
            setFilteredData(deviResponse.data);
            setJobCodes(jobCodeResponse.data);
            setOglCodes(oglResponse.data);
            setOrderCardData(orderCardResponse.data);
        } catch (error) {
            message.error("Fehler beim Laden der Projektdaten.");
        } finally {
            setIsLoading(false);
        }
    };

    const enrichJobCodesWithOrderCardData = (jobCodesToEnrich: JobCode[] = jobCodes) => {
        const jobCodeMap = new Map<string, OrderCardData[]>();
        orderCardData.forEach(card => {
            if (card.jobCodeId) {
                if (!jobCodeMap.has(card.jobCodeId)) {
                    jobCodeMap.set(card.jobCodeId, []);
                }
                jobCodeMap.get(card.jobCodeId)!.push(card);
            }
        });

        const updatedJobCodesWithFlag = jobCodesToEnrich.map(job => {
            const cards = jobCodeMap.get(job.alias) || [];
            const insideOutsideRawValues = cards.map(c => c.insideOutside).filter(Boolean);
            const insideOutsideSet = new Set(insideOutsideRawValues);
            const insideOutside =
                insideOutsideSet.size === 1
                    ? Array.from(insideOutsideSet)[0]
                    : "";
            const montageValues = cards.map(c => c.assemblyType).filter(Boolean);
            const montageSet = new Set(montageValues);
            let assemblyType = "";
            if (montageSet.size === 1) {
                assemblyType = Array.from(montageSet)[0] ?? "";
            } else if (
                montageSet.has("Eigene Montage") &&
                montageSet.has("Fremdmontage") &&
                montageSet.size === 2
            ) {
                assemblyType = "Eigen- und Fremdmontage";
            }
            return {
                ...job,
                installationLocation: insideOutside,
                assemblyType,
                _hasActiveCard: cards.some(c => c.assemblyDone === "0"),
            };
        });
        const filtered = updatedJobCodesWithFlag.filter(j => j._hasActiveCard);
        const removed = updatedJobCodesWithFlag.filter(j => !j._hasActiveCard);
        if (removed.length > 0) {
            Modal.info({
                title: "Nicht relevante Produkte",
                okText: "Schliessen",
                content: (
                    <Table
                        dataSource={removed}
                        size="small"
                        rowKey={(record) => record.alias}
                        columns={[
                            { title: "Code", dataIndex: "alias", key: "alias" },
                            { title: "Bezeichner", dataIndex: "descriptionSpez", key: "descriptionSpez" }
                        ]}
                        pagination={false}
                    />
                ),
                width: 600,
                onOk: () => {
                    setJobCodes(filtered.map(({ _hasActiveCard, ...rest }) => rest));
                }
            });
        } else {
            setJobCodes(filtered.map(({ _hasActiveCard, ...rest }) => rest));
        }
    };

    const handleFinalStep = () => {
        message.success("Projekt abgeschlossen!");
        onClose();
        onCompletion();
    };

    const steps = [
        {
            title: "Projektkonfiguration",
            content: <ProjectConfiguration
                selectedProject={selectedProject}
                setProjectConfigData={setProjectConfigData}
            />
        },
        {
            title: "Produkte",
            content: (
                <ProductApproval
                    selectedProject={selectedProject}
                    jobCodes={jobCodes}
                    setJobCodes={setJobCodes}
                />
            )
        },
        {
            title: "Devis",
            content: (
                <ConstructionSectionParser
                    selectedProject={selectedProject}
                    deviData={deviData}
                    setDeviData={setDeviData}
                    filteredData={filteredData}
                    setFilteredData={setFilteredData}
                    jobCodes={jobCodes}
                    setJobCodes={setJobCodes}
                    viewType={ViewType.NEW}
                />
            )
        },
        {
            title: "OGLs",
            content: (
                <OGLApproval
                    selectedProject={selectedProject}
                    oglCodes={oglCodes}
                    setOglCodes={setOglCodes}
                />
            )
        },
        {
            title: "Auftragskartensicht",
            content: (
                <OrderCardConfiguration
                    selectedProject={selectedProject}
                    jobCodes={jobCodes}
                    viewType={ViewType.NEW_EXTERNAL}
                    oglCodes={oglCodes}
                    orderCardData={orderCardData}
                    setOrderCardData={setOrderCardData}
                />
            )
        },
        {
            title: "Produkt Konfiguration",
            content:
                <ProductAssignment
                    jobCodes={jobCodes}
                    setJobCodes={setJobCodes}
                    viewType={ViewType.NEW_EXTERNAL}
                />
        },
        {
            title: "OGL-Daten / Abschluss",
            content: <OGLConfiguration
                setOglCodes={setOglCodes}
                oglCodes={oglCodes}
                viewType={ViewType.NEW_EXTERNAL}
                selectedProject={selectedProject}
                handleFinalStep={handleFinalStep} />
        }
    ];

    const handleSave = async () => {
        const isValid = await validateStep(6);
        if (!isValid) {
            message.error("Validierung fehlgeschlagen. Bitte prüfe die OGL-Daten.");
            return;
        }
        Modal.confirm({
            width: 600,
            title: "Willst Du die Daten speichern?",
            content: "Die Änderungen werden gespeichert und der Wizard geschlossen.",
            okText: "Ja",
            cancelText: "Abbrechen",
            onOk: async () => {
                setIsLoading(true);
                try {
                    const response = await axios.post(`${API_BASE_URL}/saveFullLegacyTransferData`, {
                        projectNo: selectedProject,
                        oglCodes: oglCodes,
                        projectData: projectConfigData,
                        jobCodes: jobCodes,
                        deviData: deviData,
                        orderCardData: orderCardData
                    });

                    if (response.status === 200) {
                        message.success(response.data.message || "Daten erfolgreich gespeichert.");
                        onClose();
                    } else {
                        message.error(response.data.message || "Fehler beim Speichern der Daten.");
                    }
                } catch (error: any) {
                    console.error("Fehler beim Speichern:", error);
                    message.error(error.response?.data?.message || "Fehler beim Speichern der Daten.");
                } finally {
                    setIsLoading(false);
                    handleFinalStep()
                }
            }
        });
    };

    const showValidationError = (title: string, errorMessage: string) => {
        const cleanedMessage = errorMessage
            .split("\n")
            .filter((line) => line.trim() !== "") // 🔥 Leere Zeilen entfernen
            .map((line, index) => <p key={index} style={{ margin: "4px 0", whiteSpace: "pre-line" }}>{line}</p>); // 🔧 Abstand reduzieren

        Modal.error({
            title,
            icon: <ExclamationCircleOutlined />,
            content: <div>{cleanedMessage}</div>,
            okText: "Schliessen"
        });
    };

    const showValidationErrorTable = (
        title: string,
        errorList: { cardName: string; error: string }[]
    ) => {
        // 🔁 Fehler nach Karte gruppieren
        const groupedErrorsMap: Record<string, string[]> = {};
        for (const entry of errorList) {
            if (!groupedErrorsMap[entry.cardName]) {
                groupedErrorsMap[entry.cardName] = [];
            }
            groupedErrorsMap[entry.cardName].push(entry.error);
        }
        const groupedErrors = Object.entries(groupedErrorsMap).map(([cardName, errors]) => ({
            cardName,
            error: errors.join('\n') // Mehrzeilig
        }));
        Modal.error({
            title,
            icon: <ExclamationCircleOutlined />,
            content: (
                <Table
                    size="small"
                    dataSource={groupedErrors}
                    columns={[
                        {
                            title: "Bezeichner",
                            dataIndex: "cardName",
                            key: "cardName",
                            width: '60%',
                            render: text => (
                                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
                            )
                        },
                        {
                            title: "Fehler",
                            dataIndex: "error",
                            key: "error",
                            width: '40%',
                            render: text => (
                                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {text}
                                </div>
                            )
                        }
                    ]}
                    pagination={false}
                    rowKey={(_, index) => index!!.toString()}
                    scroll={{ y: 400 }}
                />
            ),
            width: 1000,
            okText: "Schliessen"
        });
    };

    const validateStep = async (stepIndex: number): Promise<boolean> => {
        if (stepIndex === 0) {
            if (!projectConfigData || !projectConfigData.ordersReceiptionDate || !projectConfigData.kickOffDate || projectConfigData.approvalDuration <= 0) {
                showValidationError("Validierungsfehler", "❌ Bitte fülle alle erforderlichen Felder für die Projektkonfiguration aus.");
                return false;
            }
        }

        if (stepIndex === 1) {
            if (jobCodes.length === 0) {
                showValidationError("Validierungsfehler", "❌ Keine Produkte vorhanden.");
                return false;
            }
            try {
                const response = await axios.post(`${API_BASE_URL}/validateTransferJobCodeDefinition`, {
                    projectNo: selectedProject,
                    jobCodes: jobCodes
                });

                if (response.status !== 200) {
                    showValidationErrorTable("Validierungsfehler", response.data.message || "❌ Validierung der Produkte fehlgeschlagen.");
                    return false;
                }
            } catch (error: any) {
                showValidationErrorTable("Validierungsfehler", error.response?.data || "❌ Fehler beim Validieren der Produkte.");
                return false;
            }
        }

        if (stepIndex === 2) {
            if (deviData.length === 0) {
                showValidationError("Validierungsfehler", "❌ Es wurden keine Devi-Daten zugewiesen. Bitte ordne Produkte zu.");
                return false;
            }
            try {
                const response = await axios.post(`${API_BASE_URL}/validateTransferDeviAssignment`, {
                    projectNo: selectedProject,
                    deviData: deviData
                });

                if (response.status !== 200) {
                    showValidationErrorTable("Validierungsfehler", response.data.message || "❌ Validierung der Zuweisung Devi fehlgeschlagen.");
                    return false;
                }
            } catch (error: any) {
                showValidationErrorTable("Validierungsfehler", error.response?.data || "❌ Fehler beim Validieren der Devi-Daten.");
                return false;
            }
        }

        if (stepIndex === 3) {
            if (oglCodes.length === 0) {
                showValidationError("Validierungsfehler", "❌ Keine OGLs vorhanden.");
                return false;
            }
            try {
                const response = await axios.post(`${API_BASE_URL}/validateTransferOglDefinition`, {
                    projectNo: selectedProject,
                    oglCodes: oglCodes
                });

                if (response.status !== 200) {
                    showValidationErrorTable("Validierungsfehler", response.data.message || "❌ Validierung der OGLs fehlgeschlagen.");
                    return false;
                }
            } catch (error: any) {
                showValidationErrorTable("Validierungsfehler", error.response?.data || "❌ Fehler beim Validieren der OGLs.");
                return false;
            }
        }

        if (stepIndex === 4) {
            if (orderCardData.length === 0) {
                showValidationError("Validierungsfehler", "❌ Es wurden keine Auftragskarten geladen.");
                return false;
            }
            try {
                const response = await axios.post(`${API_BASE_URL}/validateTransferOrderCards`, {
                    projectNo: selectedProject,
                    orderCardData: orderCardData
                });

                if (response.status !== 200) {
                    showValidationErrorTable("Validierungsfehler", response.data.message || "❌ Validierung der Auftragskarten fehlgeschlagen.");
                    return false;
                }
            } catch (error: any) {
                showValidationErrorTable("Validierungsfehler", error.response?.data || "❌ Fehler beim Validieren der Auftragskarten.");
                return false;
            }
        }

        if (stepIndex === 5) {
            if (!jobCodes || jobCodes.length === 0) {
                showValidationError("Validierungsfehler", "❌ Es müssen JobCodes in der Produktkonfiguration vorhanden sein.");
                return false;
            }
            try {
                const response = await axios.post(`${API_BASE_URL}/validateTransferProductAssignment`, {
                    projectNo: selectedProject,
                    jobCodes: jobCodes,
                    orderCardData: orderCardData
                });

                if (response.status !== 200) {
                    showValidationErrorTable("Validierungsfehler", response.data.message || "❌ Validierung der Produktkonfiguration fehlgeschlagen.");
                    return false;
                }
            } catch (error: any) {
                showValidationErrorTable("Validierungsfehler", error.response?.data || "❌ Fehler beim Validieren der Produktkonfiguration.");
                return false;
            }
        }

        if (stepIndex === 6) {
            if (!oglCodes || oglCodes.length === 0) {
                showValidationError("Validierungsfehler", "❌ Die OGL-Daten müssen korrekt zugewiesen werden.");
                return false;
            }
            try {
                const response = await axios.post(`${API_BASE_URL}/validateTransferOGLData`, {
                    projectNo: selectedProject,
                    oglCodes: oglCodes,
                    jobCodes: jobCodes,
                    orderCardData: orderCardData
                });

                if (response.status !== 200) {
                    showValidationErrorTable("Validierungsfehler", response.data.message || "❌ Validierung der OGL-Daten fehlgeschlagen.");
                    return false;
                }
            } catch (error: any) {
                showValidationErrorTable("Validierungsfehler", error.response?.data || "❌ Fehler beim Validieren der OGL-Daten.");
                return false;
            }
        }

        return true;
    };

    const handleNext = async () => {
        const isValid = await validateStep(currentStep);
        if (!isValid) return;
        if (currentStep === 4) {
            try {
                const resetJobCodes = jobCodes.map(j => ({
                    ...j,
                    productOwnerHours: 0,
                    projectManagerHours: 0,
                    drawingHours: 0,
                    assemblyDays: 0,
                }));
                setJobCodes(resetJobCodes);
                const response = await axios.post(`${API_BASE_URL}/calculateJobCodeEffortDistribution`, {
                    projectNo: selectedProject,
                    jobCodes: resetJobCodes,
                    orderCardData: orderCardData
                });
                if (response.status === 200 && Array.isArray(response.data)) {
                    // enrich direkt mit den NEUEN
                    enrichJobCodesWithOrderCardData(response.data);
                    message.success("JobCodes wurden mit Aufwänden aktualisiert.");
                } else {
                    message.error("Fehler beim Verteilen der Aufwände.");
                    return;
                }
            } catch (error) {
                console.error("Fehler beim Aufruf der Aufwandsverteilung:", error);
                message.error("Fehler beim Aufruf der Aufwandsverteilung.");
                return;
            }
        }
        setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    };

    const handleCancel = () => {
        showUnsavedChangesConfirm(onClose);
    };

    return (
        <Modal
            title={<span>Bearbeitung Projektstatus {selectedProject}</span>}
            visible={visible}
            onCancel={handleCancel}
            closable={true}
            footer={null}
            width="95vw"
            style={{ top: "2.5vh", height: "95vh" }}
            styles={{ body: { height: "85vh", overflowY: "auto" } }}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
                <Tooltip title="Zurück">
                    <Button
                        icon={<LeftOutlined />}
                        onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
                        disabled={currentStep === 0 || isLoading}
                    />
                </Tooltip>
                <Steps current={currentStep} size="default" style={{ flex: 1, margin: "0 16px" }}>
                    {steps.map((step, index) => (
                        <Step key={index} title={step.title} />
                    ))}
                </Steps>
                {currentStep === steps.length - 1 ? (
                    <Tooltip title="Speichern & Schliessen">
                        <Button
                            type="primary"
                            onClick={handleSave}
                            loading={isLoading}
                        >
                            {isLoading ? "Speichert..." : "Speichern & Schliessen"}
                        </Button>
                    </Tooltip>
                ) : (
                    <Tooltip title="Weiter">
                        <Button
                            icon={<RightOutlined />}
                            type="primary"
                            onClick={handleNext}
                        />
                    </Tooltip>
                )}
            </div>
            <div style={{ height: "100%", padding: "20px" }}>
                {isLoading ? <p>Laden...</p> : steps[currentStep].content}
            </div>
        </Modal>
    );
};
export default TransferProjectWizard;